import { useState } from "react";
import useGetQuery from "@/hooks/useGetQuery";

export const DEFAULT_LLM_PLATFORMS = ["chatgpt", "gemini", "claude", "perplexity"];

const LLM_PLATFORM_LABELS = {
  chatgpt: "ChatGPT",
  gemini: "Gemini",
  claude: "Claude",
  perplexity: "Perplexity",
};

/**
 * Search engine (LLM provider) filter state, limited to the providers the
 * client's pricing tier includes.
 *
 * Returns:
 *   llmPlatforms     string[]   — providers to send as `llm_providers`
 *   setLlmPlatforms  (string[]) — update the selection (e.g. CustomDropdown onChange)
 *   engineOptions    {value, label}[] — options for the search engine dropdown
 *   isPending        bool       — true until the available providers are known;
 *                                 callers should hold requests while this is true
 *
 * If the provider lookup fails, every known provider is offered so the page
 * still works.
 */
const useLlmPlatformFilter = (clientId) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [response, isLoaded, error] = useGetQuery(
    clientId ? `${BACKEND_URL}/api/v1/client/${clientId}/llm-providers` : null,
  );
  const tierPlatforms = response?.data?.llm_providers;
  const availablePlatforms = tierPlatforms?.length
    ? tierPlatforms
    : DEFAULT_LLM_PLATFORMS;
  const isPending = Boolean(clientId) && !isLoaded && !error;

  // null = every available provider. Selections are always intersected with
  // availablePlatforms (and kept in its order) so payloads stay stable.
  const [selectedPlatforms, setSelectedPlatforms] = useState(null);
  const selectedAvailable = selectedPlatforms
    ? availablePlatforms.filter((p) => selectedPlatforms.includes(p))
    : [];
  const llmPlatforms = selectedAvailable.length
    ? selectedAvailable
    : availablePlatforms;

  const engineOptions = availablePlatforms.map((p) => ({
    value: p,
    label: LLM_PLATFORM_LABELS[p] || p,
  }));

  return {
    llmPlatforms,
    setLlmPlatforms: setSelectedPlatforms,
    engineOptions,
    isPending,
  };
};

export default useLlmPlatformFilter;
